/**
 * moving into and out of a matching session.
 *
 * SECONDARY MODE
 * - during matching we can interact with secondary markers
 * - they trigger regular set_<type> events
 * - we intercept in fsm.svelte.ts and invoke a matching.method()
 * - however, since we can't mess with appstate state (selected marker, children etc)
 * - we need to mirror / mock the state here
 */

import { appState, MAP_BREADCRUMBS_CONTEXT } from '../appstate.svelte'
import { breadcrumbs } from '$lib/organisms/Breadcrumbs/breadcrumbs.svelte'
import type { SetEvents as FSMSetEvents } from '../fsm.svelte'
import type { MapMarker } from '$mapapp/MapApp/MapView/Marker/BasicMarker.model'
import { applySelection, type AutoSelection } from './selection'
import type { States } from './model'
import type { Matching } from '../matching.svelte'

/**
 * only marker states have a `set_<state>` handler in the global fsm. any other previous_state, such
 * as `idle` from a non-marker entry path, has to fall through to the reset branch: dispatching
 * `set_idle` would match no handler and the back button would do nothing at all.
 */
const MARKER_STATES = ['pop', 'lot', 'project', 'cluster', 'request']

const entryStateBySession = new WeakMap<Matching, 'results' | 'result'>()

type SecondaryEvent = { id: string; type?: MapMarker['type']; children?: string[] }

/** fsm action for the session's `set_secondary` event */
export function enterSecondaryView(session: Matching, args: unknown[]): States | void {
	const event = args[0] as SecondaryEvent | undefined
	if (!event?.id) {
		console.error('id is required')
		return
	}

	const isCluster = event.id.startsWith('cluster-')
	const viewType = isCluster ? 'cluster' : (appState.allMarkers.get(event.id)?.type ?? event.type)
	if (!viewType) {
		console.error('no view type for', event.id)
		return
	}

	// HINT: close popup so slide-in is single source of truth
	// for the secondary view
	appState.closePopup()

	// remember the state we entered secondary from so goBack can restore it
	// chained secondary->secondary keeps the original entry context
	if (session.fsm.current !== 'secondary') {
		entryStateBySession.set(session, session.fsm.current as 'results' | 'result')
	}

	session.temp_selectedId = event.id
	session.temp_selectedType = viewType
	mirrorClusterChildren(session, isCluster ? (event.children ?? []) : [])

	return 'secondary'
}

export function clearMirroredSelection(session: Matching): void {
	session.temp_selectedId = null
	session.temp_selectedType = undefined
	session.temp_selectedChildrenIds = []
}

function mirrorClusterChildren(session: Matching, children: string[]): void {
	session.temp_selectedChildrenIds = children
}

function dismissSelectedMatch(session: Matching, autoSelect: AutoSelection): void {
	appState.closePopup()
	applySelection(session, undefined, autoSelect)
	session.autoSelectFirst = false
	session.fsm.send('set_results') // action: appState.route.clearCoordinates() + transition to 'results'
	breadcrumbs.pop(MAP_BREADCRUMBS_CONTEXT)
}

/** back-navigation out of a matching session, reached through `appState.goBack()` */
export function goBackFromMatching(session: Matching, autoSelect: AutoSelection): void {
	if (!appState.isMatching) {
		return
	}

	if (!appState.matching) {
		return
	}

	if (appState.matching.fsm.current === 'results') {
		appState.closePopup()
		if (
			appState.previous_state &&
			MARKER_STATES.includes(appState.previous_state) &&
			appState.selectedId
		) {
			const setter = `set_${appState.previous_state}` as FSMSetEvents
			// pan back to the original marker at current zoom (no zoom snap).
			// dispatch with flyTo:false so the marker handler skips its own
			// handleFlyTo (which would snap to its default zoom 15).
			const currentZoom = appState.map?.getZoom()
			appState.fsm.send(setter, {
				id: appState.selectedId,
				flyTo: false,
				modifier: 'exit_matching'
			})
			const marker = appState.allMarkers.get(appState.selectedId)
			if (marker && currentZoom !== undefined) {
				appState.handleFlyTo([marker.lon, marker.lat], currentZoom, true)
			}
			breadcrumbs.pop(MAP_BREADCRUMBS_CONTEXT)
			return
		}

		if (session.selectedMarkerType === 'miniLot') {
			breadcrumbs.pop(MAP_BREADCRUMBS_CONTEXT)
			appState.fsm.send('set_minilot', appState.matching.miniLot)
			return
		}

		breadcrumbs.pop(MAP_BREADCRUMBS_CONTEXT)
		appState.fsm.send('reset')
		return
	}

	if (appState.matching.fsm.current === 'secondary') {
		if (entryStateBySession.get(session) === 'result' && session.selectedMatch) {
			appState.matching.fsm.send('set_result', {
				id: session.selectedMatch.partner.objId,
				type: session.selectedMatch.partner.objType
			})
		} else {
			appState.matching.fsm.send('set_results')
		}
		breadcrumbs.pop(MAP_BREADCRUMBS_CONTEXT)
		entryStateBySession.delete(session)
		return
	}

	if (appState.matching.fsm.current === 'result') {
		dismissSelectedMatch(session, autoSelect)
	}
	// results can only be triggered when a marker is selected
	// hence just return to previous state
}
